'use client';

import { useQuery } from '@tanstack/react-query';
import { fetchFullUserBalances, fetchUserBalances } from '@/lib/api/user-balances/client';
import type { FullUserBalancesResult, UserBalancesResult } from '@/lib/api/user-balances/models';
import type {
  FullUserBalancesQueryParams,
  UserBalancesQueryParams,
} from '@/lib/api/user-balances/types';
import { useAuthContext } from '@/lib/auth/AuthProvider';

export const USER_BALANCES_QK = 'user-balances';
export const FULL_USER_BALANCES_QK = 'full-user-balances';

export function useUserBalances(params: UserBalancesQueryParams) {
  const { isAuthenticated } = useAuthContext();

  return useQuery<UserBalancesResult, Error>({
    queryKey: [USER_BALANCES_QK, params],
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchUserBalances(params);
    },
    enabled: isAuthenticated,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
  });
}

export function useFullUserBalances(params: FullUserBalancesQueryParams) {
  const { isAuthenticated } = useAuthContext();

  return useQuery<FullUserBalancesResult, Error>({
    queryKey: [FULL_USER_BALANCES_QK, params],
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchFullUserBalances(params);
    },
    enabled: isAuthenticated,
    staleTime: Infinity, // Data is updated via WebSocket, no need to refetch
    gcTime: 10000, // Keep unused data in cache for 10 seconds
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Only fetch on initial mount, updates come from WebSocket
    refetchOnReconnect: false, // WebSocket will handle reconnection updates
    retry: false, // Don't retry on error or empty data
  });
}
